import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, Globe, Shield, MessageSquare, Inbox } from 'lucide-react';
import { useOnboardingStore, getOnboardingSteps } from '@/stores/onboarding-store';
import { useModuleStore } from '@/stores/module-store';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { useContainerSize } from '@/hooks/use-container-size';

const STEP_ICONS: Record<string, typeof Shield> = {
  'saas-auth': Shield,
  'tpa-auth': MessageSquare,
  'incoming-sync': Inbox,
};

/**
 * Onboarding timeline — steps 1-3.
 * Steps 1-2: progress bar while auth is pending.
 * Step 3: auto-redirect to Settings > Incoming Sync and write nstep=4 to claims.
 */
export function OnboardingTimeline() {
  const { isCompact } = useContainerSize();
  const isSharedUser = useOnboardingStore((s) => s.isSharedUser);
  const nstep = useOnboardingStore((s) => s.nstep);
  const updateNstep = useOnboardingStore((s) => s.updateNstep);
  const setOnboardingRequired = useOnboardingStore((s) => s.setOnboardingRequired);
  const setPendingPromptStep = useOnboardingStore((s) => s.setPendingPromptStep);

  const saasAuthenticated = useModuleStore((s) => s.saasAuthenticated);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const setView = useUIStore((s) => s.setView);
  const setActiveSettingsTab = useUIStore((s) => s.setActiveSettingsTab);

  const steps = getOnboardingSteps(isSharedUser);

  const completedSteps = new Set<string>();
  if (saasAuthenticated) completedSteps.add('saas-auth');
  if (tpaAuthenticated || selectedAdminUserId) completedSteps.add('tpa-auth');

  const activeStepIdx = steps.findIndex((s) => !completedSteps.has(s.id));
  const currentStepDef = activeStepIdx >= 0 ? steps[activeStepIdx] : null;
  const completedCount = completedSteps.size;
  const allDone = completedCount >= steps.length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);
  const isIncomingSyncStep = currentStepDef?.id === 'incoming-sync';
  const isAuthStep = currentStepDef?.id === 'saas-auth' || currentStepDef?.id === 'tpa-auth';

  // Step 3: auto-redirect to incoming sync settings and update claim to nstep=4
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (isIncomingSyncStep && !redirectedRef.current) {
      redirectedRef.current = true;
      // Navigate to settings > incoming sync
      setActiveSettingsTab('incoming-sync');
      setView('SETTINGS');
      // Country-code (nstep=4) is bypassed — jump straight to nstep=5 so the
      // notification dialog is the only post-timeline popup.
      updateNstep(5).then(() => {
        setOnboardingRequired(false);
        setPendingPromptStep('notification');
      });
    }
  }, [isIncomingSyncStep, setView, setActiveSettingsTab, updateNstep, setOnboardingRequired, setPendingPromptStep]);

  // When step 3 triggers, don't render anything (settings loads below)
  if (isIncomingSyncStep) return null;

  // All timeline steps done without hitting step 3 (shared user skipped it)
  if (allDone) {
    // For shared users who skip incoming sync, complete onboarding directly
    if (!redirectedRef.current) {
      redirectedRef.current = true;
      // Skip the country-code prompt — advance to nstep=5 (notification).
      updateNstep(5).then(() => {
        setOnboardingRequired(false);
        setPendingPromptStep('notification');
      });
    }
    return null;
  }

  // Steps 1-2: Show progress bar
  if (!isAuthStep) return null;

  return (
    <div className="shrink-0 z-40">
      <div className="flex items-center gap-3 px-3 py-2 border-b border-primary/20 bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
        <div className="flex items-center gap-1 shrink-0">
          {steps.map((step) => {
            const done = completedSteps.has(step.id);
            const active = step.id === currentStepDef?.id;
            const Icon = STEP_ICONS[step.id] || Globe;
            return (
              <div
                key={step.id}
                className={`flex items-center justify-center rounded-full transition-all duration-300 ${
                  isCompact ? 'h-5 w-5' : 'h-6 w-6'
                } ${
                  done
                    ? 'bg-primary text-primary-foreground'
                    : active
                      ? 'bg-primary/20 text-primary ring-2 ring-primary/30'
                      : 'bg-muted text-muted-foreground'
                }`}
                title={step.label}
              >
                {done ? <Check className="h-3 w-3" strokeWidth={3} /> : <Icon className="h-3 w-3" />}
              </div>
            );
          })}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 rounded-full bg-border overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-primary"
                initial={false}
                animate={{ width: `${progressPercent}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
            <span className="text-[10px] font-medium text-muted-foreground shrink-0">
              {completedCount}/{steps.length}
            </span>
          </div>
          {!isCompact && currentStepDef && (
            <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{currentStepDef.label}</p>
          )}
        </div>
      </div>
    </div>
  );
}
